// Firebase App Check failures (e.g. blocked reCAPTCHA, attestation rejection by
// the backend) surface either as `appCheck/*` errors thrown locally during
// token retrieval, or as `auth/firebase-app-check-token-is-invalid` /
// `auth/captcha-check-failed` from Auth when the token is missing/rejected.
// Some loaders also report a generic `internal-error` whose message references
// App Check or attestation, so we sniff the message string as a fallback.
export function isAppCheckError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: unknown }).code;
  const message = (err as { message?: unknown }).message;
  if (typeof code === "string") {
    if (code.startsWith("appCheck/")) return true;
    if (
      code === "auth/firebase-app-check-token-is-invalid" ||
      code === "auth/captcha-check-failed"
    ) {
      return true;
    }
  }
  if (typeof message === "string") {
    const lower = message.toLowerCase();
    if (
      lower.includes("app check") ||
      lower.includes("app attestation") ||
      lower.includes("appcheck")
    ) {
      return true;
    }
  }
  return false;
}

/** Stable, PII-free reason suitable for analytics and error categorization. */
export function getAuthErrorCode(error: unknown): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string"
  ) {
    return error.code.slice(0, 100);
  }

  return isAppCheckError(error) ? "app_check" : "unknown";
}

const MAGIC_LINK_EMAIL_MISMATCH_CODES = new Set([
  "auth/invalid-credential",
  "auth/invalid-email",
  "auth/user-mismatch",
]);

const RETRYABLE_AUTH_ERROR_CODES = new Set([
  "auth/internal-error",
  "auth/network-request-failed",
  "auth/too-many-requests",
]);

export function isMagicLinkEmailMismatchError(error: unknown): boolean {
  return MAGIC_LINK_EMAIL_MISMATCH_CODES.has(getAuthErrorCode(error));
}

export function isRetryableAuthError(error: unknown): boolean {
  return RETRYABLE_AUTH_ERROR_CODES.has(getAuthErrorCode(error));
}

// Mirrors DISPOSABLE_EMAIL_BLOCKED_MARKER in functions/src/disposableEmail.ts.
const DISPOSABLE_EMAIL_BLOCKED_MARKER = "DISPOSABLE_EMAIL_BLOCKED";

// True when account creation was rejected by the `blockDisposableEmailSignup`
// blocking function. Firebase Auth reports every blocking-function rejection
// as `auth/internal-error` and only forwards the function's message text, so
// the marker in that text is the only way to tell this block apart from a
// genuine server failure. Must be checked before `isRetryableAuthError`,
// which would otherwise classify it as transient.
export function isDisposableEmailBlockedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const message = (error as { message?: unknown }).message;
  return (
    typeof message === "string" &&
    message.includes(DISPOSABLE_EMAIL_BLOCKED_MARKER)
  );
}

export type MagicLinkCallbackFailure =
  | "disposable"
  | "email_mismatch"
  | "retryable"
  | "app_check"
  | "expired"
  | "invalid"
  | "unknown";

// One ordered classification of a `signInWithEmailLink` failure, so the
// callback route makes a single decision instead of chaining matchers whose
// order is easy to get wrong. The order is the contract:
//   1. disposable - the block arrives as `auth/internal-error`, which the
//      retryable set would otherwise claim; retrying can never succeed.
//   2. email_mismatch / retryable - recoverable in place by re-entering the
//      address, so they win over the terminal screens.
//   3. everything else is terminal.
export function classifyMagicLinkCallbackError(
  error: unknown
): MagicLinkCallbackFailure {
  if (isDisposableEmailBlockedError(error)) return "disposable";
  if (isMagicLinkEmailMismatchError(error)) return "email_mismatch";
  if (isRetryableAuthError(error)) return "retryable";
  if (isAppCheckError(error)) return "app_check";

  const code = getAuthErrorCode(error);
  if (code === "auth/expired-action-code") return "expired";
  if (code === "auth/invalid-action-code") return "invalid";
  return "unknown";
}

// True when an error means the request carried no valid auth credential
// (expired or revoked session), as opposed to a network or server failure.
// DataConnectError uses bare codes ("unauthorized") while FirebaseError uses
// prefixed ones ("dataconnect/unauthorized").
//
// "functions/unauthenticated" is deliberately NOT matched: callables enforce
// App Check, and an App Check rejection surfaces with the same code — a token
// refresh cannot repair that, so treating it as a credential failure would
// sign out users whose auth session is perfectly valid (e.g. blocked
// reCAPTCHA). Use isAppCheckError for that class.
export function isAuthCredentialError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  // Data Connect also surfaces an invalid App Check token as a 401 with the
  // same "unauthorized" code — only the message names App Check. An ID-token
  // refresh cannot repair that either.
  if (isAppCheckError(error)) {
    return false;
  }
  const code = (error as { code?: string }).code;
  return code === "unauthorized" || code === "dataconnect/unauthorized";
}
